import React, { useMemo, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import Card from "../components/Card";
import Button from "../components/Button";
import IconContainer from "../components/IconContainer";
import Sidebar from "../components/Sidebar";
import Input from "../components/Input";
import {
  WalletIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChartIcon,
  XIcon,
} from "../components/Icons";
import { walletAPI } from "../services/api";
import Toast from "../components/Toast";
import { getApiErrorMessage } from "../utils/apiError";

const Wallet = () => {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [depositAddress, setDepositAddress] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [withdrawOTP, setWithdrawOTP] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [networkFee, setNetworkFee] = useState(0);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalPending: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const transactionsPerPage = 10;

  const isCreditTransaction = (txType, amount) => {
    const normalizedType = String(txType || "").toUpperCase();
    if (
      normalizedType.includes("WITHDRAW") ||
      normalizedType.includes("DEBIT") ||
      normalizedType.includes("FEE")
    ) {
      return false;
    }
    if (
      normalizedType.includes("DEPOSIT") ||
      normalizedType.includes("CREDIT") ||
      normalizedType.includes("BONUS") ||
      normalizedType.includes("REWARD") ||
      normalizedType.includes("COMMISSION")
    ) {
      return true;
    }
    return Number(amount || 0) >= 0;
  };

  const formatSignedAmount = (tx) => {
    const rawAmount = Number(tx?.amount || 0);
    const sign = isCreditTransaction(tx?.type, rawAmount) ? "+" : "-";
    return `${sign}$${Math.abs(rawAmount).toFixed(2)}`;
  };

  const formatTransactionType = (tx) => {
    const type = String(tx?.type || "").toLowerCase();
    const customLabels = {
      bot_license_fee: "Bot License Fee",
      affiliate_commission: "Affiliate Commission",
      arbitrage_trade_close: "Arbitrage Trade Settlement",
      arbitrage_profit: "Arbitrage Profit",
      crypto_deposit: "Crypto Deposit",
      crypto_withdrawal: "Crypto Withdrawal",
      deposit: "Deposit",
      withdraw: "Withdrawal",
      debit: "Debit",
      credit: "Credit",
      leadership_reward: "Leadership Reward",
      performance_award: "Performance Award",
    };
    if (customLabels[type]) return customLabels[type];
    return String(tx?.network || tx?.type || "Transaction")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const visibleTransactions = useMemo(
    () => transactions.filter((tx) => Math.abs(Number(tx?.amount || 0)) > 0),
    [transactions],
  );

  // Fetch wallet data
  useEffect(() => {
    fetchWalletData();
  }, [currentPage, dateFilter]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams({
        page: currentPage,
        limit: transactionsPerPage,
      });
      
      if (dateFilter.startDate) params.append('startDate', dateFilter.startDate);
      if (dateFilter.endDate) params.append('endDate', dateFilter.endDate);
      
      const [walletData, transactionsData] = await Promise.all([
        walletAPI.getWallet(),
        walletAPI.getTransactions(params.toString()),
      ]);

      setBalance(walletData.wallet.balance);
      setStats({
        totalDeposits: walletData.statistics.totalDeposits,
        totalWithdrawals: walletData.statistics.totalWithdrawals,
        totalPending: walletData.statistics.totalPending,
      });
      setTransactions(
        (transactionsData.transactions || []).filter(
          (tx) => Math.abs(Number(tx?.amount || 0)) > 0,
        ),
      );
      setTotalPages(Math.ceil((transactionsData.total || transactionsData.transactions?.length || 0) / transactionsPerPage));
    } catch (error) {
      showMessage(
        "error",
        getApiErrorMessage(error, "Failed to load wallet data"),
      );
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    const variant = type === "error" ? "error" : "success";
    setToast({ show: true, message: text, variant });
    setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      variant === "error" ? 8000 : 4500,
    );
  };

  const handleDeposit = async () => {
    if (!selectedNetwork) {
      showMessage("error", "Please select a network");
      return;
    }

    try {
      const response = await walletAPI.deposit(0, selectedNetwork);
      setDepositAddress(response.transaction.address);
      showMessage("success", "Deposit address generated successfully");
    } catch (error) {
      showMessage(
        "error",
        getApiErrorMessage(error, "Failed to generate deposit address"),
      );
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      showMessage("error", "Please enter a valid amount");
      return;
    }
    if (!selectedNetwork) {
      showMessage("error", "Please select a network");
      return;
    }
    if (!withdrawAddress) {
      showMessage("error", "Please enter withdrawal address");
      return;
    }

    try {
      // Request OTP
      const response = await walletAPI.requestWithdrawalOTP(
        parseFloat(withdrawAmount),
        selectedNetwork,
        withdrawAddress
      );
      
      setNetworkFee(response.networkFee || 0);
      setOtpExpiry(response.expiresAt);
      setShowWithdrawModal(false);
      setShowOTPModal(true);
      showMessage("success", response.message || "OTP sent to your email");
    } catch (error) {
      showMessage(
        "error",
        getApiErrorMessage(error, "Failed to request withdrawal"),
      );
    }
  };

  const handleVerifyOTP = async () => {
    if (!withdrawOTP || withdrawOTP.length !== 6) {
      showMessage("error", "Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await walletAPI.verifyWithdrawalOTP(withdrawOTP);
      showMessage("success", response.message || "Withdrawal submitted successfully");
      
      // Reset form
      setWithdrawAmount("");
      setWithdrawAddress("");
      setSelectedNetwork("");
      setWithdrawOTP("");
      setShowOTPModal(false);
      
      // Refresh data
      fetchWalletData();
    } catch (error) {
      showMessage(
        "error",
        getApiErrorMessage(error, "OTP verification failed"),
      );
    }
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setWithdrawAmount("");
    setWithdrawAddress("");
    setSelectedNetwork("");
  };

  const closeOTPModal = () => {
    setShowOTPModal(false);
    setWithdrawOTP("");
  };

  const closeDepositModal = () => {
    setShowDepositModal(false);
    setSelectedNetwork("");
    setDepositAddress("");
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 md:p-8 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Wallet
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Manage your funds and transactions
          </p>
        </div>

        <Toast
          message={toast.message}
          show={toast.show}
          variant={toast.variant}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-[var(--text-secondary)]">Loading...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Balance Card */}
            <Card>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                  <p className="text-[var(--text-primary)] text-sm mb-2">Total Balance</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)]">
                    ${balance.toFixed(2)}
                  </h2>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowDepositModal(true)}
                    className="flex items-center gap-2"
                  >
                    <ArrowDownIcon className="w-4 h-4" />
                    Deposit
                  </Button>
                  <Button
                    onClick={() => setShowWithdrawModal(true)}
                    className="flex items-center gap-2 bg-surface-soft hover:bg-white/20"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                    Withdraw
                  </Button>
                </div>
              </div>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[var(--text-primary)] text-xs mb-2">Total Deposits</p>
                    <p className="text-xl font-bold text-[var(--text-primary)] mb-1">
                      ${stats.totalDeposits.toFixed(2)}
                    </p>
                  </div>
                  <IconContainer size="sm">
                    <ArrowDownIcon className="w-5 h-5 text-green-400" />
                  </IconContainer>
                </div>
              </Card>

              <Card>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[var(--text-primary)] text-xs mb-2">Total Withdrawals</p>
                    <p className="text-xl font-bold text-[var(--text-primary)] mb-1">
                      ${stats.totalWithdrawals.toFixed(2)}
                    </p>
                  </div>
                  <IconContainer size="sm">
                    <ArrowUpIcon className="w-5 h-5 text-red-400" />
                  </IconContainer>
                </div>
              </Card>

              <Card>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[var(--text-primary)] text-xs mb-2">Pending</p>
                    <p className="text-xl font-bold text-[var(--text-primary)] mb-1">
                      ${stats.totalPending.toFixed(2)}
                    </p>
                  </div>
                  <IconContainer size="sm">
                    <ChartIcon className="w-5 h-5 text-primary" />
                  </IconContainer>
                </div>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)]">
                  Recent Transactions
                </h3>
                
                {/* Date Filter */}
                <div className="flex flex-col md:flex-row gap-3">
                  <div>
                    <label className="block text-xs text-[var(--text-secondary)] mb-1">From Date</label>
                    <input
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) => {
                        setDateFilter({ ...dateFilter, startDate: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 bg-surface-softer border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-secondary)] mb-1">To Date</label>
                    <input
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) => {
                        setDateFilter({ ...dateFilter, endDate: e.target.value });
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 bg-surface-softer border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
                    />
                  </div>
                  {(dateFilter.startDate || dateFilter.endDate) && (
                    <button
                      onClick={() => {
                        setDateFilter({ startDate: "", endDate: "" });
                        setCurrentPage(1);
                      }}
                      className="self-end px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                {visibleTransactions.length === 0 ? (
                  <div className="text-center py-8 text-[var(--text-secondary)]">
                    No transactions yet
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[var(--border-color)]">
                          <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-primary)]">Transaction ID</th>
                          <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-primary)]">Date</th>
                          <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-primary)]">Amount</th>
                          <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-primary)]">Method</th>
                          <th className="text-left py-4 px-4 text-sm font-medium text-[var(--text-primary)]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleTransactions.map((tx) => (
                          <tr key={tx.id} className="border-b border-white/5 hover:bg-surface-softer transition-colors">
                            <td className="py-4 px-4 text-sm text-[var(--text-primary)] font-medium">
                              TXN{tx.id}
                            </td>
                            <td className="py-4 px-4 text-sm text-[var(--text-primary)]">
                              {new Date(tx.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              }).split('/').join('-')}
                            </td>
                            <td
                              className={`py-4 px-4 text-sm font-semibold ${
                                formatSignedAmount(tx).startsWith("+")
                                  ? "text-green-400"
                                  : "text-red-400"
                              }`}
                            >
                              {formatSignedAmount(tx)}
                            </td>
                            <td className="py-4 px-4 text-sm text-[var(--text-primary)]">
                              {formatTransactionType(tx)}
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className={`inline-block px-4 py-1 rounded-full text-xs font-medium ${
                                  tx.status === "COMPLETED" || tx.status === "confirmed" || tx.status === "completed"
                                    ? "bg-green-500/20 text-green-400 border border-green-400/30"
                                    : tx.status === "PENDING" || tx.status === "pending"
                                    ? "bg-[#F57C00]/20 text-[#FFA726] border border-[#FFA726]/30"
                                    : tx.status === "processing" || tx.status === "Processing"
                                    ? "bg-[#1976D2]/20 text-[#42A5F5] border border-[#42A5F5]/30"
                                    : "bg-[#C62828]/20 text-[#EF5350] border border-[#EF5350]/30"
                                }`}
                              >
                                {(tx.status === "COMPLETED" || tx.status === "confirmed" || tx.status === "completed") 
                                  ? "Completed"
                                  : tx.status?.charAt(0).toUpperCase() + tx.status?.slice(1).toLowerCase() || "N/A"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--border-color)]">
                        <div className="text-sm text-[var(--text-secondary)]">
                          Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === 1
                                ? 'bg-surface-softer text-white/30 cursor-not-allowed'
                                : 'bg-[#22c55e] text-[var(--text-primary)] hover:bg-[#16a34a]'
                            }`}
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === totalPages
                                ? 'bg-surface-softer text-white/30 cursor-not-allowed'
                                : 'bg-[#22c55e] text-[var(--text-primary)] hover:bg-[#16a34a]'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Deposit Modal */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)]">Deposit Funds</h3>
                <button
                  onClick={closeDepositModal}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              {depositAddress ? (
                <div className="space-y-4">
                  {/* QR Code Display */}
                  <div className="flex justify-center bg-white p-4 rounded-lg">
                    <QRCodeSVG
                      value={depositAddress}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>

                  <div className="bg-surface-softer p-4 rounded-lg">
                    <p className="text-xs text-[var(--text-secondary)] mb-2">
                      Deposit Address ({selectedNetwork})
                    </p>
                    <p className="text-sm font-mono text-[var(--text-primary)] break-all">
                      {depositAddress}
                    </p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(depositAddress);
                        showMessage("success", "Address copied to clipboard!");
                      }}
                      className="mt-2 text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      📋 Copy Address
                    </button>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/50 p-3 rounded-lg">
                    <p className="text-xs text-yellow-400">
                      📱 Scan the QR code with your crypto wallet or copy the address
                      above. Send any amount of USDT to this address using the{" "}
                      {selectedNetwork} network. Your deposit will be credited
                      automatically.
                    </p>
                  </div>
                  <Button
                    onClick={closeDepositModal}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-[var(--text-secondary)] mb-2">
                      Select Network
                    </label>
                    <select
                      value={selectedNetwork}
                      onChange={(e) => setSelectedNetwork(e.target.value)}
                      className="w-full bg-surface-softer border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary"
                    >
                      <option value="">Choose Network</option>
                      <option value="TRC20">TRC20 (USDT - Tron)</option>
                      <option value="BEP20">BEP20 (USDT - BSC)</option>
                      <option value="OPBNB">OPBNB (USDT - opBNB)</option>
                    </select>
                  </div>
                  <Button onClick={handleDeposit} className="w-full">
                    Generate QR Code
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Withdraw Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)]">Withdraw Funds</h3>
                <button
                  onClick={closeWithdrawModal}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">Network</label>
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="w-full bg-surface-softer border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary"
                  >
                    <option value="">Select Network</option>
                    <option value="TRC20">TRC20 (USDT - Tron)</option>
                    <option value="BEP20">BEP20 (USDT - BSC)</option>
                    <option value="OPBNB">OPBNB (USDT - opBNB)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    Amount (USD)
                  </label>
                  <Input
                    type="number"
                    placeholder="Enter amount"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    min="1"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    Withdrawal Address
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your wallet address"
                    value={withdrawAddress}
                    onChange={(e) => setWithdrawAddress(e.target.value)}
                  />
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/50 p-3 rounded-lg">
                  <p className="text-xs text-yellow-400">
                    ⚠️ Double-check your wallet address. Incorrect addresses may
                    result in permanent loss of funds. After verification, your
                    withdrawal is processed on-chain automatically.
                  </p>
                </div>
                <Button onClick={handleWithdraw} className="w-full">
                  Request Withdrawal OTP
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* OTP Verification Modal */}
        {showOTPModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)]">Verify OTP</h3>
                <button
                  onClick={closeOTPModal}
                  className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-500/10 border border-blue-500/50 p-4 rounded-lg">
                  <p className="text-sm text-blue-400 mb-2">
                    📧 We've sent a 6-digit OTP to your registered email address.
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {otpExpiry && `Expires: ${new Date(otpExpiry).toLocaleTimeString()}`}
                  </p>
                </div>

                <div className="bg-surface-softer p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[var(--text-secondary)]">Amount:</span>
                      <span className="text-[var(--text-primary)] font-semibold ml-2">${parseFloat(withdrawAmount).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)]">Network Fee:</span>
                      <span className="text-[var(--text-primary)] font-semibold ml-2">${networkFee.toFixed(2)}</span>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-[var(--border-color)]">
                      <span className="text-[var(--text-secondary)]">Total Amount:</span>
                      <span className="text-green-400 font-bold ml-2 text-lg">
                        ${(parseFloat(withdrawAmount) + networkFee).toFixed(2)}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-[var(--text-secondary)]">Network:</span>
                      <span className="text-[var(--text-primary)] font-semibold ml-2">{selectedNetwork}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-[var(--text-secondary)]">To Address:</span>
                      <p className="text-[var(--text-primary)] font-mono text-xs mt-1 break-all">{withdrawAddress}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[var(--text-secondary)] mb-2">
                    Enter 6-Digit OTP
                  </label>
                  <Input
                    type="text"
                    placeholder="000000"
                    value={withdrawOTP}
                    onChange={(e) => setWithdrawOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength="6"
                    className="text-center text-2xl tracking-widest"
                  />
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/50 p-3 rounded-lg">
                  <p className="text-xs text-yellow-400">
                    ⚠️ After OTP verification, the total (including network fee) is deducted
                    from your wallet and your withdrawal is sent for on-chain processing.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={closeOTPModal}
                    className="flex-1 bg-surface-soft hover:bg-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleVerifyOTP}
                    className="flex-1"
                    disabled={withdrawOTP.length !== 6}
                  >
                    Verify & Submit
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
